import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { TDashboard } from 'types/GlobalDashboard'
import { TFetchStatusEntry } from 'redux/reducers/Dashboards'
import { toArray } from 'redux/utils'

/**
 * Select all dashboards in a dashboard group (sorted by index).
 * Returns empty array if no dashboards are found in the group.
 *
 * @param state - Application state
 * @param dashboardGroupId - Dashboard group ID
 * @returns Array of dashboards sorted by index
 */
export const selectDashboardsInGroup = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (_: ApplicationState, dashboardGroupId?: string | null) => dashboardGroupId
  ],
  (dashboards, dashboardGroupId): TDashboard[] => {
    if (!dashboardGroupId) return []

    return toArray(dashboards)
      .filter((dashboard) => dashboard.dashboard_group_id === dashboardGroupId)
      .sort((a, b) => a.index - b.index)
  }
)

/**
 * Select all dashboards in the same dashboard group as the given dashboard.
 * Returns empty array if dashboard is not found.
 *
 * @param state - Application state
 * @param dashboardId - Dashboard ID
 * @returns Array of dashboards in the same group (sorted by index)
 */
export const selectDashboardsInSameGroup = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (_: ApplicationState, dashboardId: string | null) => dashboardId
  ],
  (dashboards, dashboardId): TDashboard[] => {
    if (!dashboardId) return []

    const dashboard = dashboards[dashboardId]

    if (!dashboard) return []

    // Get all dashboards in the same group
    return toArray(dashboards)
      .filter((d) => d.dashboard_group_id === dashboard.dashboard_group_id)
      .sort((a, b) => a.index - b.index)
  }
)

/**
 * Select a specific dashboard by ID.
 * Returns null if dashboard is not found.
 *
 * @param state - Application state
 * @param dashboardId - Dashboard ID
 * @returns Dashboard or null
 */
export const selectDashboard = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.dashboards,
    (_: ApplicationState, dashboardId: string | null) => dashboardId
  ],
  (dashboards, dashboardId): TDashboard | null => {
    if (!dashboardId) return null

    return dashboards[dashboardId] ?? null
  }
)

/**
 * Select all dashboards as an array.
 *
 * @param state - Application state
 * @returns Array of all dashboards
 */
export const selectDashboards = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.dashboards
  ],
  (dashboards): TDashboard[] => {
    return toArray(dashboards)
  }
)

/**
 * Select fetch status for a specific dashboard.
 * Returns { status: 'idle' } if dashboard has not been fetched.
 *
 * @param state - Application state
 * @param dashboardId - Dashboard ID
 * @returns Fetch status entry with status and optional errorStatusCode
 */
export const selectDashboardFetchStatus = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.KPIDashboardStore.fetchStatus,
    (_: ApplicationState, dashboardId: string | null) => dashboardId
  ],
  (fetchStatus, dashboardId): TFetchStatusEntry => {
    if (!dashboardId) return { status: 'idle' }

    return fetchStatus[dashboardId] ?? { status: 'idle' }
  }
)
